import { Link } from "react-router-dom";
import { MapPin, Phone } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import ImageFrame from "@/components/site/ImageFrame";
import Reveal from "@/components/site/Reveal";
import SectionHeading from "@/components/site/SectionHeading";
import Seo from "@/components/site/Seo";
import PageHeader from "@/components/site/PageHeader";

export default function Borgo() {
  const { settings } = useSettings();
  return (
    <>
      <Seo
        title="Il borgo di Aterrana · Osteria Aterrana, Montoro (AV)"
        description="Aterrana è un borgo storico di pietra sulle colline di Montoro, in Irpinia. Strade strette, portali antichi e una piccola osteria che vuole farlo vivere."
        path="/il-borgo"
      />
      <PageHeader
        eyebrow="Il borgo"
        title="Aterrana, un borgo di pietra"
        lead="Sulle colline di Montoro, in provincia di Avellino. Si arriva, si parcheggia e il resto si fa a piedi."
        label="BORGO PHOTO"
        alt="Il borgo storico di Aterrana"
        src={settings.borgo_header_image}
      />

      <section className="section-y bg-cream">
        <div className="container-ed grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5 lg:sticky lg:top-32 self-start">
            <SectionHeading eyebrow="Dove siamo" title={settings.borgo_title || "Aterrana, Montoro e l'Irpinia"} />
          </div>
          <Reveal className="lg:col-span-6 lg:col-start-7 prose-ed text-sm md:text-lg text-foreground/80">
            {(settings.borgo_text || "").split("\n\n").map((p) => (
              <p key={p.slice(0, 60)}>{p}</p>
            ))}
            <p>
              Montoro è la terra della cipolla ramata; intorno c'è l'Irpinia del Fiano, del Greco e del
              Taurasi, le montagne, i santuari, i paesi che si guardano da una collina all'altra. Aterrana
              sta nel mezzo, tranquilla.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-beige/50 py-24 md:py-32">
        <div className="container-ed grid gap-6 md:grid-cols-12">
          <Reveal className="md:col-span-7">
            <ImageFrame
              src={settings.borgo_image_1}
              label="BORGO PHOTO"
              alt="Vicolo acciottolato del borgo di Aterrana"
              ratio="4 / 3"
            />
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-5 md:mt-16">
            <ImageFrame
              src={settings.borgo_image_2}
              label="BORGO PHOTO"
              alt="Una finestra fiorita nel borgo"
              ratio="3 / 4"
            />
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-5 md:col-start-2">
            <ImageFrame
              src={settings.borgo_image_3}
              label="COURTYARD PHOTO"
              alt="Il cortile con il pozzo storico del borgo"
              ratio="1 / 1"
            />
          </Reveal>
          <Reveal delay={0.2} className="md:col-span-6 self-end">
            <blockquote className="font-serif text-2xl md:text-[2.4rem] leading-[1.2] text-wood pl-8 border-l border-gold">
              Un piccolo progetto che cresce insieme ad Aterrana.
            </blockquote>
          </Reveal>
        </div>
      </section>

      <section className="section-y bg-cream">
        <div className="container-ed grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <SectionHeading
              eyebrow="Perché siamo qui"
              title="Un motivo in più per venire ad Aterrana"
              lead="Vorremmo che l'osteria fosse una delle ragioni per cui qualcuno decide di salire fin qui, attraversare il borgo a piedi e tornarci."
            />
          </div>
          <div className="lg:col-span-5 lg:col-start-8 space-y-8 border-l border-wood/15 pl-8">
            {[
              ["A piedi", "Il borgo si gira in pochi minuti: vicoli, scale, archi, il pozzo nel cortile."],
              ["La sera", "Poche luci e silenzio. Nelle sere buone, un calice tra le pietre."],
              ["Intorno", "Solofra, Mercato San Severino, Fisciano, Avellino e la Valle dell'Irno a pochi minuti."],
            ].map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.08}>
                <p className="eyebrow text-gold">{t}</p>
                <p className="mt-2 text-sm md:text-base text-foreground/70 leading-relaxed">{d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-wood-deep grain text-cream py-24 md:py-28">
        <div className="container-ed grid gap-12 lg:grid-cols-12 items-center">
          <div className="lg:col-span-5">
            <p className="eyebrow text-gold">Come arrivare</p>
            <h2 className="mt-5 font-serif text-3xl md:text-4xl">Osteria Aterrana</h2>
            <p className="mt-5 text-sm text-cream/70 leading-relaxed">
              {settings.address || "Via Madonna di Montevergine 34"}
              <br />
              {settings.address_line2 || "Borgo storico di Aterrana · Montoro (AV)"}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={settings.maps_url || "#"}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="borgo-maps"
                className="inline-flex items-center gap-2 px-7 py-4 bg-gold text-wood-deep text-[0.7rem] uppercase tracking-[0.2em] hover:bg-cream transition-colors duration-500"
              >
                <MapPin size={14} /> Indicazioni
              </a>
              <a
                href={`tel:${settings.phone_link || "+393311091971"}`}
                className="inline-flex items-center gap-2 px-7 py-4 border border-cream/35 text-cream text-[0.7rem] uppercase tracking-[0.2em] hover:bg-cream hover:text-wood transition-colors duration-500"
              >
                <Phone size={14} /> {settings.phone || "331 10 91 971"}
              </a>
            </div>
            <Link
              to="/prenota"
              className="mt-6 inline-block text-[0.7rem] uppercase tracking-[0.2em] text-cream/70 link-underline"
            >
              Prenota un tavolo
            </Link>
          </div>
          <Reveal className="lg:col-span-6 lg:col-start-7">
            <div className="border border-cream/15">
              <iframe
                title="Mappa di Aterrana, Montoro"
                src="https://www.openstreetmap.org/export/embed.html?bbox=14.75%2C40.79%2C14.83%2C40.85&amp;layer=mapnik"
                className="w-full h-[320px] md:h-[400px] grayscale-[35%]"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
